import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Table,
  Popconfirm,
  Modal,
  DatePicker,
} from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { API } from "../../config/Api";
import { GET, POST, PUT, DELETE } from "../../utils/apiRequest";
import dayjs from "dayjs";

const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface Category {
  id: number;
  name: string;
}

interface ExpenseData {
  id: number;
  category?: {
    id: number;
    name: string;
  };
  FinanceCategory?: {
    id: number;
    name: string;
  };
  amount: number;
  note: string;
  created_at: string;
}

const Expense = () => {
  const [form] = Form.useForm();
  const user = useSelector((state: any) => state?.User?.user);
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState<ExpenseData[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseData | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [dateFilter, setDateFilter] = useState<string>("today");
  const [customDateRange, setCustomDateRange] = useState<any>(null);

  useEffect(() => {
    getCategories();
    // Load today's expenses by default
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);
    getExpenses("", "All", {
      startDate: today.toISOString(),
      endDate: todayEnd.toISOString(),
    });
  }, []);

  const getCategories = async () => {
    try {
      const response: any = await GET(
        API.GET_FINANCE_CATEGORY_BY_TYPE + "expense?officeId=" + user?.office?.id,
        null
      );
      if (response?.status) {
        setCategories(response?.data || []);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  const getExpenses = async (
    searchQuery = "",
    categoryFilter = "All",
    dateFilterObj: any = null
  ) => {
    try {
      setTableLoading(true);

      // Build query parameters
      let queryParams: string[] = [`officeId=${user?.office?.id}`];

      // Add search query if exists
      if (searchQuery && searchQuery.trim() !== "") {
        queryParams.push(`query=${encodeURIComponent(searchQuery)}`);
      }

      // Add date filter if exists
      if (dateFilterObj?.startDate && dateFilterObj?.endDate) {
        queryParams.push(`startDate=${encodeURIComponent(dateFilterObj.startDate)}`);
        queryParams.push(`endDate=${encodeURIComponent(dateFilterObj.endDate)}`);
      }

      // Add category filter if not 'All'
      if (categoryFilter !== "All") {
        const selectedCat = categories.find((c) => c.name === categoryFilter);
        if (selectedCat) {
          queryParams.push(`categoryId=${selectedCat.id}`);
        }
      }

      // Build final URL with all query parameters
      const url =
        API.GET_FINANCE_TRANSACTION_BY_TYPE + "expense?" + queryParams.join("&");

      const response: any = await GET(url, null);
      if (response?.status) {
        setExpenses(response?.data || []);
      } else {
        setExpenses([]);
      }
    } catch (error) {
      console.error("Error fetching expenses:", error);
      setExpenses([]);
    } finally {
      setTableLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      const requestPayload: any = {
        category_id: parseInt(values?.expense_category),
        type: "expense",
        amount: parseFloat(values?.expense_amount),
        note: values?.note,
        office_id: parseInt(user?.office?.id),
      };

      let response: any;
      if (editingExpense) {
        requestPayload.id = parseInt(String(editingExpense?.id));
        // Update existing expense
        response = await PUT(API.UPDATE_FINANCE_TRANSACTION, requestPayload);
      } else {
        // Create new expense
        requestPayload.created_by = parseInt(user?.user?.id || user?.id);
        response = await POST(API.CREATE_FINANCE_TRANSACTION, requestPayload);
      }

      if (response?.status) {
        message.success(
          editingExpense
            ? "Expense updated successfully"
            : "Expense added successfully"
        );
        form.resetFields();
        setEditingExpense(null);
        setIsModalOpen(false);
        // Refresh both lists with current filters
        const dateRange =
          dateFilter === "custom" && customDateRange
            ? {
                startDate: customDateRange[0].toDate().toISOString(),
                endDate: customDateRange[1].toDate().toISOString(),
              }
            : getDateRangeFromFilter(dateFilter);
        await Promise.all([
          getExpenses(searchText, selectedCategory, dateRange),
          getCategories(),
        ]);
      } else {
        message.error(
          response?.message ||
            (editingExpense ? "Failed to update expense" : "Failed to add expense")
        );
      }

      setLoading(false);
    } catch (error) {
      console.error("Error saving expense:", error);
      message.error("Something went wrong");
      setLoading(false);
    }
  };

  const handleEdit = async (expense: ExpenseData) => {
    setEditingExpense(expense);
    form.setFieldsValue({
      expense_category: expense?.category?.id || expense?.FinanceCategory?.id,
      expense_amount: expense?.amount,
      note: expense?.note,
    });
    // Refresh categories to get the latest list
    await getCategories();
    setIsModalOpen(true);
  };

  const handleAddNew = async () => {
    setEditingExpense(null);
    form.resetFields();
    // Refresh categories to get the latest list
    await getCategories();
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      setTableLoading(true);
      const response: any = await DELETE(API.DELETE_FINANCE_TRANSACTION + id);
      if (response?.status) {
        message.success("Expense deleted successfully");
        // Refresh with current filters
        const dateRange =
          dateFilter === "custom" && customDateRange
            ? {
                startDate: customDateRange[0].toDate().toISOString(),
                endDate: customDateRange[1].toDate().toISOString(),
              }
            : getDateRangeFromFilter(dateFilter);
        await getExpenses(searchText, selectedCategory, dateRange);
      } else {
        message.error("Failed to delete expense");
      }
    } catch (error) {
      console.error("Error deleting expense:", error);
      message.error("Something went wrong");
    } finally {
      setTableLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setEditingExpense(null);
    form.resetFields();
  };

  const getDateRangeFromFilter = (filter: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let start: Date, end: Date;

    switch (filter) {
      case "today":
        start = new Date(today);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      case "yesterday":
        start = new Date(today.getTime() - 86400000);
        end = new Date(today.getTime() - 86400000);
        end.setHours(23, 59, 59, 999);
        break;
      case "this_month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      case "last_month":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        end.setHours(23, 59, 59, 999);
        break;
      case "last_30_days":
        start = new Date(today.getTime() - 2592000000);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        start = new Date(today);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
    }

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  };

  const handleDateFilterChange = (value: string) => {
    setDateFilter(value);
    if (value !== "custom") {
      const dateRange = getDateRangeFromFilter(value);
      getExpenses(searchText, selectedCategory, dateRange);
    }
  };

  const handleCustomDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      const start = dates[0].toDate();
      const end = dates[1].toDate();
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      setCustomDateRange(dates);
      const dateRange = {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      };
      getExpenses(searchText, selectedCategory, dateRange);
    }
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    const dateRange =
      dateFilter === "custom" && customDateRange
        ? {
            startDate: customDateRange[0].toDate().toISOString(),
            endDate: customDateRange[1].toDate().toISOString(),
          }
        : getDateRangeFromFilter(dateFilter);
    getExpenses(searchText, value, dateRange);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    const dateRange =
      dateFilter === "custom" && customDateRange
        ? {
            startDate: customDateRange[0].toDate().toISOString(),
            endDate: customDateRange[1].toDate().toISOString(),
          }
        : getDateRangeFromFilter(dateFilter);
    getExpenses(value, selectedCategory, dateRange);
  };

  const handleClearFilters = () => {
    setSearchText("");
    setSelectedCategory("All");
    setDateFilter("today");
    setCustomDateRange(null);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);
    getExpenses("", "All", {
      startDate: today.toISOString(),
      endDate: todayEnd.toISOString(),
    });
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "id",
      key: "id",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (_: any, record: any) => record?.category?.name || record?.FinanceCategory?.name || "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => `₹${Number(amount)?.toFixed(2)}`,
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: ExpenseData) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this expense?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="screenBox">
      <Card>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          paddingBottom: "16px",
          borderBottom: "2px solid #f0f0f0"
        }}>
          <h5 className="user-heading" style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>
            Expense Management
          </h5>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
            size="large"
          >
            Add Expense
          </Button>
        </div>

        {/* Filter Section */}
        <Card
          size="small"
          style={{
            marginBottom: "20px",
            backgroundColor: "#fafafa",
            border: "1px solid #e8e8e8",
            padding: "8px"
          }}
        >
          <Row gutter={16}>
            <Col flex="300px">
              <Input
                placeholder="Search by note..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                allowClear
                size="large"
              />
            </Col>
            <Col flex={dateFilter === "custom" ? "200px" : "220px"}>
              <Select
                style={{ width: "100%" }}
                placeholder="Select Date Range"
                value={dateFilter}
                onChange={handleDateFilterChange}
                size="large"
              >
                <Select.Option value="today">Today</Select.Option>
                <Select.Option value="yesterday">Yesterday</Select.Option>
                <Select.Option value="this_month">This Month</Select.Option>
                <Select.Option value="last_month">Last Month</Select.Option>
                <Select.Option value="last_30_days">Last 30 Days</Select.Option>
                <Select.Option value="custom">Custom Date</Select.Option>
              </Select>
            </Col>
            {dateFilter === "custom" && (
              <Col flex="300px">
                <RangePicker
                  style={{ width: "100%" }}
                  value={customDateRange}
                  onChange={handleCustomDateChange}
                  size="large"
                  placeholder={["Start Date", "End Date"]}
                />
              </Col>
            )}
            <Col flex={dateFilter === "custom" ? "180px" : "220px"}>
              <Select
                style={{ width: "100%" }}
                placeholder="All Categories"
                value={selectedCategory}
                onChange={handleCategoryChange}
                size="large"
              >
                <Select.Option value="All">All Categories</Select.Option>
                {categories.map((cat) => (
                  <Select.Option key={cat.id} value={cat.name}>
                    {cat.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col flex="120px">
              <Button
                onClick={handleClearFilters}
                style={{ width: "100%" }}
                size="large"
                danger
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Card>

        <Table
          columns={columns}
          dataSource={expenses}
          loading={tableLoading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} expenses`,
          }}
          bordered
          size="middle"
          style={{ marginTop: "8px" }}
        />
      </Card>

      <Modal
        title={editingExpense ? "Edit Expense" : "Add Expense"}
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          hideRequiredMark
        >
          <Row>
            <Col md={12}>
              <Form.Item
                name="expense_category"
                label="Expense Category"
                rules={[
                  {
                    required: true,
                    message: "Please select expense category",
                  },
                ]}
              >
                <Select
                  placeholder="Select Category"
                  showSearch
                  optionFilterProp="label"
                >
                  {categories.map((category: any) => (
                    <Select.Option
                      key={category.id}
                      value={category.id}
                      label={category.name}
                    >
                      {category.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item
                name="expense_amount"
                label="Expense Amount"
                rules={[
                  {
                    required: true,
                    message: "Please enter expense amount",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  className="w-100"
                  min={0}
                  prefix="₹"
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item
                name="note"
                label="Note"
                rules={[
                  {
                    required: true,
                    message: "Please enter note",
                  },
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder="Enter expense note or details"
                />
              </Form.Item>
            </Col>
          </Row>
          <div className="text-end">
            <Button
              type="default"
              onClick={handleModalCancel}
              style={{ marginRight: "8px" }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingExpense ? "Update Expense" : "Add Expense"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Expense;
